/**
 * Reading-beginner word pools — the 250-word journey curricula that drive the progression mode.
 * See specs/word-mastery-progression.md §2 and specs/fables-lesson.md.
 *
 * A pool reuses the `Pack` shape: one set (`all`) of 250 words, each carrying a stable `id` and
 * frequency `rank`. The progression engine (src/game/progression) owns the per-player mastery over
 * these words; the pool itself is static curriculum data.
 *
 * The journey families today: the default `reading-250` (most-common words), the themed
 * `fables-250` (fairy-tale vocabulary, ends on a Unicorn Star), `dailylife` (everyday topics,
 * ends on a growing earth) and `adventure` (dinosaurs, planets, pirates — the exciting topics).
 * All drive boards and progression; arcade packs (src/data/packs) do not. Only languages with a
 * pool file get that journey; others fall back to the random arcade packs (see RoundScene).
 */
import type { Pack } from '@/data/packs/types';
import readingDe from './reading-250.de.json';
import readingChDe from './reading-250.de-CH.json';
import readingEn from './reading-250.en.json';
import readingFr from './reading-250.fr.json';
import readingEs from './reading-250.es.json';
import readingIt from './reading-250.it.json';
import readingNl from './reading-250.nl.json';
import readingJa from './reading-250.ja.json';
import readingKo from './reading-250.ko.json';
import readingTh from './reading-250.th.json';
import fablesDe from './fables-250.de.json';
import fablesChDe from './fables-250.de-CH.json';
import fablesEn from './fables-250.en.json';
import fablesFr from './fables-250.fr.json';
import fablesEs from './fables-250.es.json';
import fablesIt from './fables-250.it.json';
import fablesNl from './fables-250.nl.json';
import fablesJa from './fables-250.ja.json';
import fablesKo from './fables-250.ko.json';
import fablesTh from './fables-250.th.json';
import dailylifeEn from './dailylife.en.json';
import dailylifeDe from './dailylife.de.json';
import dailylifeChDe from './dailylife.de-CH.json';
import dailylifeFr from './dailylife.fr.json';
import dailylifeEs from './dailylife.es.json';
import dailylifeIt from './dailylife.it.json';
import dailylifeNl from './dailylife.nl.json';
import dailylifeJa from './dailylife.ja.json';
import dailylifeKo from './dailylife.ko.json';
import dailylifeTh from './dailylife.th.json';
import adventureEn from './adventure.en.json';
import adventureDe from './adventure.de.json';
import adventureChDe from './adventure.de-CH.json';
import adventureFr from './adventure.fr.json';
import adventureEs from './adventure.es.json';
import adventureIt from './adventure.it.json';
import adventureNl from './adventure.nl.json';
import adventureJa from './adventure.ja.json';
import adventureKo from './adventure.ko.json';
import adventureTh from './adventure.th.json';
import halloweenEn from './halloween.en.json';
import halloweenDe from './halloween.de.json';
import halloweenChDe from './halloween.de-CH.json';
import pictureWordsEn from './picturewords.en.json';
import pictureWordsDe from './picturewords.de.json';
import pictureWordsChDe from './picturewords.de-CH.json';
import pictureWordsFr from './picturewords.fr.json';
import pictureWordsEs from './picturewords.es.json';
import pictureWordsIt from './picturewords.it.json';
import pictureWordsNl from './picturewords.nl.json';
import pictureWordsJa from './picturewords.ja.json';
import pictureWordsKo from './picturewords.ko.json';
import pictureWordsTh from './picturewords.th.json';
import artikelDe from './artikel.de.json';
import artikelChDe from './artikel.de-CH.json';
import ieDe from './ie.de.json';
import ieChDe from './ie.de-CH.json';
import doppeltDe from './doppelt.de.json';
import doppeltChDe from './doppelt.de-CH.json';
import josaKo from './josa.ko.json';
import uimunsaKo from './uimunsa.ko.json';
import uimunsaJa from './uimunsa.ja.json';
import auslautDe from './auslaut.de.json';
import auslautChDe from './auslaut.de-CH.json';
import digraphsEn from './digraphs.en.json';
import magicEEn from './magic-e.en.json';
import lettreMuetteFr from './lettre-muette.fr.json';
import aOuAFr from './a-ou-a.fr.json';
import erreEs from './erre.es.json';
import acentoEs from './acento.es.json';
import verlengNl from './verleng.nl.json';
import werkwoordNl from './werkwoord.nl.json';

/** Reading-journey pools keyed by exact BCP-47 tag. */
const READING_POOLS: Record<string, Pack> = {
  de: readingDe as Pack,
  'de-CH': readingChDe as Pack,
  en: readingEn as Pack,
  fr: readingFr as Pack,
  es: readingEs as Pack,
  it: readingIt as Pack,
  nl: readingNl as Pack,
  ja: readingJa as Pack,
  ko: readingKo as Pack,
  th: readingTh as Pack,
};

/** Fables-journey pools keyed by exact BCP-47 tag (a subset of languages ship this lesson). */
const FABLES_POOLS: Record<string, Pack> = {
  de: fablesDe as Pack,
  'de-CH': fablesChDe as Pack,
  en: fablesEn as Pack,
  fr: fablesFr as Pack,
  es: fablesEs as Pack,
  it: fablesIt as Pack,
  nl: fablesNl as Pack,
  ja: fablesJa as Pack,
  ko: fablesKo as Pack,
  th: fablesTh as Pack,
};

/**
 * Daily-life journey pools keyed by exact BCP-47 tag. A topic curriculum (colors, shapes,
 * animals, …) that gates topics 4-at-a-time and rewards a growing earth planet. Ships all 8
 * app languages, generated from one master list (scripts/gen-dailylife-pools.mjs) so every
 * language shares word ids/ranks; only the displayed text differs. See specs/category-topic-modes.md.
 */
const DAILYLIFE_POOLS: Record<string, Pack> = {
  en: dailylifeEn as Pack,
  de: dailylifeDe as Pack,
  'de-CH': dailylifeChDe as Pack,
  fr: dailylifeFr as Pack,
  es: dailylifeEs as Pack,
  it: dailylifeIt as Pack,
  nl: dailylifeNl as Pack,
  ja: dailylifeJa as Pack,
  ko: dailylifeKo as Pack,
  th: dailylifeTh as Pack,
};

/**
 * Adventure journey pools keyed by exact BCP-47 tag. The "exciting" topic curriculum — big cats,
 * predator dinosaurs, planet names, mythical creatures — the counterpart to Daily Life's everyday
 * vocabulary. Topics are narrow on purpose so a board rule ("Smash all the big cats!") pulls its
 * decoys from the neighbouring topics. Other languages fall back to the remaining journeys until
 * their pool ships.
 */
const ADVENTURE_POOLS: Record<string, Pack> = {
  en: adventureEn as Pack,
  de: adventureDe as Pack,
  'de-CH': adventureChDe as Pack,
  fr: adventureFr as Pack,
  es: adventureEs as Pack,
  it: adventureIt as Pack,
  nl: adventureNl as Pack,
  ja: adventureJa as Pack,
  ko: adventureKo as Pack,
  th: adventureTh as Pack,
};

/**
 * Halloween event pools (en, de, de-CH). A seasonal lesson: it is NOT part of journeyPoolsForLang —
 * packs.ts adds it to the picker only while its event window is open (src/game/season.ts).
 * Generated by scripts/gen-halloween-pools.mjs.
 */
const HALLOWEEN_POOLS: Record<string, Pack> = {
  en: halloweenEn as Pack,
  de: halloweenDe as Pack,
  'de-CH': halloweenChDe as Pack,
};

/**
 * Picture Words journey pools keyed by exact BCP-47 tag. The banner shows a PICTURE and the child
 * smashes the word that names it — icons are keyed on the language-independent word id, so one icon
 * set serves every language. See specs/picture-word-mode.md.
 *
 * Generated from one master list by scripts/gen-picturewords-pools.mjs, so every language shares
 * word ids/ranks/classes and only the text differs — which is exactly what lets one icon set serve
 * all ten.
 *
 * ⚠️ WORK IN PROGRESS — only the `pets` topic is authored (10 of a planned 150 words, plus 8
 * never-pictured decoys). Enough to play the mode and judge how it feels, NOT a shippable lesson.
 * Do not release until the remaining 14 topics are authored and the translations natively reviewed.
 */
const PICTUREWORDS_POOLS: Record<string, Pack> = {
  en: pictureWordsEn as Pack,
  de: pictureWordsDe as Pack,
  'de-CH': pictureWordsChDe as Pack,
  fr: pictureWordsFr as Pack,
  es: pictureWordsEs as Pack,
  it: pictureWordsIt as Pack,
  nl: pictureWordsNl as Pack,
  ja: pictureWordsJa as Pack,
  ko: pictureWordsKo as Pack,
  th: pictureWordsTh as Pack,
};

/**
 * Article journey pools — the ONLY lesson that is not language-neutral by design. It drills noun
 * gender (der/die/das), which exists as a three-way choice in German and nowhere else in the app's
 * ten languages, so the map deliberately holds `de` and `de-CH` only; every other language gets
 * `null` from resolvePool and simply never sees the lesson. Generated by
 * scripts/gen-artikel-pools.mjs. See specs/artikel-lesson.md.
 */
const ARTIKEL_POOLS: Record<string, Pack> = {
  de: artikelDe as Pack,
  'de-CH': artikelChDe as Pack,
};

/**
 * Spelling journey pools — like the article lesson, German-only by design. It drills the long i
 * (`B_ne` → Biene vs. `F_sch` → Fisch), a choice <ie> forces on German alone: Dutch writes long i
 * as <ie> without ever making the writer decide, and French/Spanish/Italian have no length
 * opposition to spell. Generated by scripts/gen-ie-pools.mjs. See specs/ie-lesson.md.
 */
const IE_POOLS: Record<string, Pack> = {
  de: ieDe as Pack,
  'de-CH': ieChDe as Pack,
};

/**
 * The second spelling journey, German-only like "i oder ie?": the doubled consonant (`So_ne` →
 * Sonne vs. `Kro_ne` → Krone, with ck and tz as its third stage). Italian drills doubling too, but
 * by ear on the consonant rather than the vowel before it — a sister lesson with its own spec, not
 * a translation. Generated by scripts/gen-doppelt-pools.mjs. See specs/doppelkonsonant-lesson.md.
 */
const DOPPELT_POOLS: Record<string, Pack> = {
  de: doppeltDe as Pack,
  'de-CH': doppeltChDe as Pack,
};

/**
 * The first Korean lesson: which particle family a noun takes (`책_` → 이·을·은, `사과_` → 가·를·는),
 * which is decided by nothing but the noun's last 받침. Korean only by nature — no other language
 * in the app splits its particles on a final consonant. Generated by scripts/gen-josa-pools.mjs.
 * See specs/josa-lesson.md.
 */
const JOSA_POOLS: Record<string, Pack> = {
  ko: josaKo as Pack,
};

/**
 * The question-word lesson: which of 누구 · 무엇 · 어디 · 언제 a word answers (엄마 → 누구, 학교 →
 * 어디), and in Japanese which of だれ・なに・どこ・いつ (おかあさん → だれ, がっこう → どこ). The one
 * lesson here that is not tied to its language: same four classes, same four stages, same help — only
 * the word lists differ, and with them the traps each language has to exclude (spec §10.1). Generated
 * by scripts/gen-uimunsa-pools.mjs. See specs/uimunsa-lesson.md.
 */
/**
 * The Verlängern lesson, "Weich oder hart?": at the end of a word b, d and g sound hard like p, t
 * and k (`Hun_` is Hund, `Zel_` is Zelt), and the child lengthens the word until the letter comes
 * back. Five letter classes, each banner asking for one of them and each board holding two. German-only
 * like the other three spelling lessons — Dutch hardens its endings the same way, but its own
 * problem is a different one (d/t/dt on verbs), so that would be a sister lesson with its own word
 * list. Generated by scripts/gen-auslaut-pools.mjs. See specs/auslaut-lesson.md.
 */
const AUSLAUT_POOLS: Record<string, Pack> = {
  de: auslautDe as Pack,
  'de-CH': auslautChDe as Pack,
};

const UIMUNSA_POOLS: Record<string, Pack> = {
  ko: uimunsaKo as Pack,
  ja: uimunsaJa as Pack,
};

/**
 * The first English phonics lesson, "sh, ch or th?": two letters, one sound. The tile hides the
 * pair (`_ark` → shark, `lun_` → lunch, `mo_er` → mother) and each banner asks for one of the
 * three. English only — the digraphs are English spelling; German's own sch/ch/st question is
 * "St oder Scht?". Generated by scripts/gen-digraphs-pools.mjs. See specs/digraphs-lesson.md.
 */
const DIGRAPHS_POOLS: Record<string, Pack> = {
  en: digraphsEn as Pack,
};

/**
 * The second English phonics lesson, "Magic e?": the silent e that makes the vowel say its name
 * (`cak_` → cake vs. `cat_` → cat). The English sister of "Eins oder zwei?" — same two-class gap
 * with an empty-hole class. Generated by scripts/gen-magic-e-pools.mjs. See specs/magic-e-lesson.md.
 */
const MAGIC_E_POOLS: Record<string, Pack> = {
  en: magicEEn as Pack,
};

/**
 * The first French spelling lesson, "Quelle lettre muette ?": the last letter French writes and does
 * not say (`peti_` → petit, `gran_` → grand, `gri_` → gris), found by making the word longer until it
 * is heard (petite, grande, grise). The French sister of „Weich oder hart?" — same strategy, one
 * letter per banner. Generated by scripts/gen-lettre-muette-pools.mjs. See specs/lettre-muette-lesson.md.
 */
const LETTRE_MUETTE_POOLS: Record<string, Pack> = {
  fr: lettreMuetteFr as Pack,
};

/**
 * The second French lesson, "a ou à ?": the verb and the preposition that sound the same. The tiles
 * are short PHRASES (`il _ faim`, `il va _ l'école`), because a bare a or à means nothing on its own;
 * the help teaches the classroom swap test (if "avait" fits, write a). Generated by
 * scripts/gen-a-ou-a-pools.mjs. See specs/a-ou-a-lesson.md.
 */
const A_OU_A_POOLS: Record<string, Pack> = {
  fr: aOuAFr as Pack,
};

/**
 * The first Spanish spelling lesson, "¿r o rr?": the tile hides the r (`bu_o` → burro, `pája_o` →
 * pájaro, `_atón` → ratón) and each banner asks for one r or two. rr stands only between two vowels
 * when the r sounds strong; the traps are the strong r at the start of a word and after n, l, s,
 * which is written single. The same for every Spanish accent. Generated by
 * scripts/gen-erre-pools.mjs. See specs/erre-lesson.md.
 */
const ERRE_POOLS: Record<string, Pack> = {
  es: erreEs as Pack,
};

/**
 * The second Spanish lesson, "¿Aguda, llana o esdrújula?": whole words (canción, árbol, música)
 * sorted by the syllable that carries the stress — the step every tilde rule is built on. The only
 * lesson whose answer is computed from the tile: Spanish spelling marks stress completely.
 * Generated by scripts/gen-acento-pools.mjs. See specs/acento-lesson.md.
 */
const ACENTO_POOLS: Record<string, Pack> = {
  es: acentoEs as Pack,
};

/**
 * The first Dutch spelling lesson, "d of t?": the verlengwoord. A final d sounds like a t (hond says
 * "hont"); the tile drops the last letter (`hon_`, `ka_`) and the child makes the word longer until
 * it is heard (honden, katten). The Dutch sister of „Weich oder hart?". Generated by
 * scripts/gen-verleng-pools.mjs. See specs/verleng-lesson.md.
 */
const VERLENG_POOLS: Record<string, Pack> = {
  nl: verlengNl as Pack,
};

/**
 * The second Dutch lesson, "d, t of dt?": present-tense werkwoordspelling (ik word, hij wordt, word
 * jij?). The tiles are short PHRASES, because the ending depends on the subject and the word order;
 * the answer is computed from subject + stem by the school rule, never written down. Generated by
 * scripts/gen-werkwoord-pools.mjs. See specs/werkwoord-lesson.md.
 */
const WERKWOORD_POOLS: Record<string, Pack> = {
  nl: werkwoordNl as Pack,
};

/** Resolve a pool from a map: exact tag first (`de-CH` → Swiss), then base prefix (`de-AT` → `de`). */
function resolvePool(map: Record<string, Pack>, lang: string): Pack | null {
  if (map[lang]) return map[lang];
  const prefix = lang.split('-')[0];
  return map[prefix] ?? null;
}

export function getReadingPool(lang: string): Pack | null {
  return resolvePool(READING_POOLS, lang);
}

export function getFablesPool(lang: string): Pack | null {
  return resolvePool(FABLES_POOLS, lang);
}

export function getDailyLifePool(lang: string): Pack | null {
  return resolvePool(DAILYLIFE_POOLS, lang);
}

export function getAdventurePool(lang: string): Pack | null {
  return resolvePool(ADVENTURE_POOLS, lang);
}

export function getPictureWordsPool(lang: string): Pack | null {
  return resolvePool(PICTUREWORDS_POOLS, lang);
}

export function getArtikelPool(lang: string): Pack | null {
  return resolvePool(ARTIKEL_POOLS, lang);
}

export function getIePool(lang: string): Pack | null {
  return resolvePool(IE_POOLS, lang);
}

export function getDoppeltPool(lang: string): Pack | null {
  return resolvePool(DOPPELT_POOLS, lang);
}

export function getJosaPool(lang: string): Pack | null {
  return resolvePool(JOSA_POOLS, lang);
}

export function getAuslautPool(lang: string): Pack | null {
  return resolvePool(AUSLAUT_POOLS, lang);
}

export function getUimunsaPool(lang: string): Pack | null {
  return resolvePool(UIMUNSA_POOLS, lang);
}

export function getDigraphsPool(lang: string): Pack | null {
  return resolvePool(DIGRAPHS_POOLS, lang);
}

export function getMagicEPool(lang: string): Pack | null {
  return resolvePool(MAGIC_E_POOLS, lang);
}

export function getLettreMuettePool(lang: string): Pack | null {
  return resolvePool(LETTRE_MUETTE_POOLS, lang);
}

export function getAOuAPool(lang: string): Pack | null {
  return resolvePool(A_OU_A_POOLS, lang);
}

export function getErrePool(lang: string): Pack | null {
  return resolvePool(ERRE_POOLS, lang);
}

export function getAcentoPool(lang: string): Pack | null {
  return resolvePool(ACENTO_POOLS, lang);
}

export function getVerlengPool(lang: string): Pack | null {
  return resolvePool(VERLENG_POOLS, lang);
}

export function getWerkwoordPool(lang: string): Pack | null {
  return resolvePool(WERKWOORD_POOLS, lang);
}

export function getHalloweenPool(lang: string): Pack | null {
  return resolvePool(HALLOWEEN_POOLS, lang);
}

export function hasReadingPool(lang: string): boolean {
  return getReadingPool(lang) !== null;
}

/** Pool/pack ids that drive the progression journey (vs. arcade packs). */
export const READING_POOL_ID = 'reading-250';
export const FABLES_POOL_ID = 'fables-250';
export const DAILYLIFE_POOL_ID = 'dailylife';
export const ADVENTURE_POOL_ID = 'adventure';
export const PICTUREWORDS_POOL_ID = 'picturewords';
export const ARTIKEL_POOL_ID = 'artikel';
export const IE_POOL_ID = 'ie';
export const DOPPELT_POOL_ID = 'doppelt';
export const JOSA_POOL_ID = 'josa';
export const AUSLAUT_POOL_ID = 'auslaut';
export const UIMUNSA_POOL_ID = 'uimunsa';
export const DIGRAPHS_POOL_ID = 'digraphs';
export const MAGIC_E_POOL_ID = 'magic-e';
export const LETTRE_MUETTE_POOL_ID = 'lettre-muette';
export const A_OU_A_POOL_ID = 'a-ou-a';
export const ERRE_POOL_ID = 'erre';
export const ACENTO_POOL_ID = 'acento';
export const VERLENG_POOL_ID = 'verleng';
export const WERKWOORD_POOL_ID = 'werkwoord';
export const HALLOWEEN_POOL_ID = 'halloween';
export const JOURNEY_POOL_IDS: readonly string[] = [
  READING_POOL_ID, FABLES_POOL_ID, DAILYLIFE_POOL_ID, ADVENTURE_POOL_ID, PICTUREWORDS_POOL_ID,
  ARTIKEL_POOL_ID, IE_POOL_ID, DOPPELT_POOL_ID, AUSLAUT_POOL_ID, UIMUNSA_POOL_ID, JOSA_POOL_ID,
  DIGRAPHS_POOL_ID, MAGIC_E_POOL_ID, LETTRE_MUETTE_POOL_ID, A_OU_A_POOL_ID,
  ERRE_POOL_ID, ACENTO_POOL_ID, VERLENG_POOL_ID, WERKWOORD_POOL_ID, HALLOWEEN_POOL_ID,
];

/** True for the growing-earth-planet journey — drives the procedural planet reward visual. */
export function isGrowingPlanetPool(pack: { id: string }): boolean {
  return pack.id === DAILYLIFE_POOL_ID;
}

/** True for the reading-250 pool specifically — used only for the legacy mastery-key migration. */
export function isReadingPool(pack: { id: string }): boolean {
  return pack.id === READING_POOL_ID;
}

/** True for any journey pool (reading or fables) — drives boards + progression rather than arcade. */
export function isJourneyPool(pack: { id: string }): boolean {
  return JOURNEY_POOL_IDS.includes(pack.id);
}

/**
 * All journey pools available for a language, in lesson-picker order: Daily Life first (the
 * default lesson — everyday topics are the gentlest start), then Adventure, Fables and the
 * reading journey. Picture Words trails the four (still work in progress, English only), and
 * The German-only lessons come last, in the order a child meets them at school: "Der, Die, Das"
 * (grammar), then "i oder ie?", "Eins oder zwei?" and "Weich oder hart?" (spelling, the lessons that
 * expect a reader — minAge 7). All of them need the vocabulary the other lessons build. Korean's "이? 가?" takes the
 * same place in the Korean picker, behind „누구? 어디?" — sorting words by the question they answer
 * needs no 받침, so it stands in front of the lesson that does. Neither is ever the default. The
 * English phonics lessons close the English picker the same way, in school order: "sh, ch or th?"
 * (reception/kindergarten), then "Magic e?" (year 1/first grade). French likewise: "Quelle lettre
 * muette ?" (CP–CE1), then "a ou à ?" (CE1–CE2). Spanish too: "¿r o rr?" (1º–2º de primaria),
 * then "¿Aguda, llana o esdrújula?" (3º–4º). And Dutch: "d of t?" (groep 4), then "d, t of dt?"
 * (groep 6).
 * The first entry of this list is what `defaultLessonForCurrentLang()` picks (src/game/packs.ts).
 */
export function journeyPoolsForLang(lang: string): Pack[] {
  return [
    getDailyLifePool(lang), getAdventurePool(lang), getFablesPool(lang),
    getReadingPool(lang), getPictureWordsPool(lang), getArtikelPool(lang), getIePool(lang),
    getDoppeltPool(lang), getAuslautPool(lang), getUimunsaPool(lang), getJosaPool(lang),
    getDigraphsPool(lang), getMagicEPool(lang), getLettreMuettePool(lang), getAOuAPool(lang),
    getErrePool(lang), getAcentoPool(lang), getVerlengPool(lang), getWerkwoordPool(lang),
  ].filter((p): p is Pack => p !== null);
}
